// Contenuto delle 13 aree del cruscotto.
//
// REGOLA STRUTTURALE: un'area è DATO, non markup. Aggiungere un'area significa
// aggiungere una voce a questo oggetto e un id a GRUPPI_NAV — mai scrivere una
// nuova schermata. È ciò che tiene i numeri coerenti fra le aree e lo Stato
// piattaforma: esistono in un solo posto.

export type Stato = 'ok' | 'att' | 'ko' | 'neutro';
export type Tono = 'ok' | 'att' | 'ko' | 'mono' | 'neutro' | undefined;

/** [testo, tono] — 'mono' rende il valore in tabellare, non è un colore. */
export type Cella = [string, Tono?];

export interface Riga {
  nome: string;
  sotto: string;
  stato: Stato;
  celle: Cella[];
  azione: string;
}

export interface Blocco {
  tipo?: 'tabella' | 'schede' | 'lista';
  titolo: string;
  nota: string;
  azione?: string;
  colonne?: string[];
  righe?: Riga[];
  /** [titolo, valore, nota, tono] */
  schede?: [string, string, string, Stato][];
  /** [nome, nota, valore, tono] */
  voci?: [string, string, string, Tono][];
}

export interface Area {
  gruppo: string;
  nome: string;
  titolo: string;
  lede: string;
  /** [etichetta, genere, href?] — con href diventa un link, non un bottone */
  azioni: [string, 'primaria' | 'secondaria', string?][];
  /** [etichetta, valore, nota, tono?] */
  kpi: [string, string, string, Tono?][];
  /** [gravita, titolo, testo, azione] */
  avvisi: ['ko' | 'att', string, string, string][];
  blocchi: Blocco[];
}

/* ============ LE AREE DEL CRUSCOTTO ============
   Una voce di navigazione = un'area. Il contenuto è dichiarato qui e reso da
   un'unica vista: aggiungere un'area significa aggiungere una voce a questo
   oggetto, non scrivere markup nuovo.
   I numeri sono coerenti con lo Stato piattaforma: se cambiano là, cambiano qui. */

export const C: Record<Stato, string> = { ok: '#1d6b48', att: '#e8b829', ko: '#c0392b', neutro: '#9aa0ad' };
export const MONO = "'IBM Plex Mono', monospace";
export const T = { ok: '#5d6472', att: '#7a5c00', ko: '#8f261a' };

/** Riga di tabella. Il tono delle celle è una stringa, non un colore:
 *  il colore lo decide il renderer, così una nuova palette non tocca i dati. */
export function riga(nome: string, sotto: string, stato: Stato, celle: Cella[], azione?: string): Riga {
  return { nome, sotto, stato, celle, azione: azione ?? 'Apri' };
}

export const AREE: Record<string, Area> = {
  /* ---------- 1 · IDENTITÀ E ACCESSI ---------- */
  'id-idp': {
    gruppo: '1 · IDENTITÀ E ACCESSI', nome: 'Identità digitale e IdP',
    titolo: 'Identità digitale e Identity Provider',
    lede: 'Con quali identità si entra in UranIA e quale livello di garanzia serve per ciascun ruolo. Il livello di assurance non è una preferenza: determina che cosa un utente può fare.',
    azioni: [['Aggiungi un provider', 'primaria'], ['Prova un accesso', 'secondaria']],
    kpi: [
      ['Provider attivi', '3', 'SPID, CIE, CNS su Keycloak'],
      ['Utenti abilitati', '65', 'su 3 tenant'],
      ['Accessi nelle 24h', '128', 'nessun tentativo anomalo'],
      ['Ultimo handshake', '21:57', 'esito positivo'],
    ],
    blocchi: [
      { titolo: 'Provider configurati', nota: 'La corrispondenza fra metodo di accesso e ruolo è vincolante: è la stessa che la schermata di login mostra all\'utente.',
        colonne: ['PROVIDER', 'RUOLO AMMESSO', 'ASSURANCE', 'ULTIMO ESITO', ''],
        righe: [
          riga('SPID', 'Sistema Pubblico di Identità Digitale — 9 gestori', 'ok', [['Funzionario AdG'], ['L2', 'mono'], ['21:57 · positivo']], 'Configura'),
          riga('CIE', 'Carta d\'Identità Elettronica — NFC o app CieID', 'ok', [['Beneficiario'], ['L2', 'mono'], ['20:14 · positivo']], 'Configura'),
          riga('CNS', 'Smart card della PA — riservata al ruolo Auditor', 'att', [['Auditor'], ['L3', 'mono'], ['nessun accesso da 6 giorni', 'att']], 'Configura'),
          riga('Credenziali tecniche', 'Solo in collaudo: gli IdP non sono attivi in staging', 'neutro', [['tutti i profili'], ['—', 'mono'], ['in uso su urania-test']], 'Gestisci'),
        ] },
      { tipo: 'lista', titolo: 'Regole di accesso', nota: 'Valgono per tutti i tenant e non sono derogabili dal singolo ente.',
        voci: [
          ['Assurance minima per la redazione di atti', 'Un funzionario non può redigere con un\'identità di livello inferiore.', 'L2', 'ok'],
          ['Assurance minima per l\'accesso al fascicolo di audit', 'Sola lettura, ma su dati che fondano decisioni: serve L3.', 'L3', 'ok'],
          ['Durata della sessione', 'Oltre questo tempo si rientra con l\'identità digitale.', '8 ore', 'ok'],
          ['Tentativi prima del blocco', 'Blocco temporaneo di 15 minuti, registrato negli eventi.', '5', 'ok'],
          ['Ruolo Auditor nel realm', 'Previsto dal progetto, non ancora creato: nessuna guardia lo autorizza.', 'assente', 'att'],
        ] },
    ],
    avvisi: [['att', 'Il ruolo Auditor non esiste ancora nel realm', 'La verifica di conformità è progettata ma nessuno può esercitarla: finché il ruolo non è creato in Keycloak, la CNS non ha destinazione.', 'Crea il ruolo']],
  },

  'id-tenant': {
    gruppo: '1 · IDENTITÀ E ACCESSI', nome: 'Tenant, ruoli, permessi',
    titolo: 'Tenant, ruoli e permessi',
    lede: 'Ogni ente è un tenant separato: propri corpora, propri format degli atti, propri tetti di spesa. Un permesso concesso qui si vede immediatamente in ciò che gli utenti possono fare.',
    azioni: [['Nuovo tenant', 'primaria'], ['Esporta la matrice', 'secondaria']],
    kpi: [
      ['Tenant attivi', '3', 'un ente, due organismi intermedi'],
      ['Ruoli definiti', '4', 'più uno previsto e non attivo'],
      ['Permessi', '6', 'assegnati per ruolo, non per persona'],
      ['Modifiche in 7 giorni', '2', 'tracciate nel registro eventi'],
    ],
    blocchi: [
      { titolo: 'Tenant', nota: 'Isolamento dei dati garantito a livello di corpus e di gateway: nessuna risposta attraversa il confine fra tenant.',
        colonne: ['TENANT', 'UTENTI', 'CORPORA', 'TETTO DI SPESA', ''],
        righe: [
          riga('Autorità di Gestione', 'Tenant principale — programmazione e attuazione', 'ok', [['41', 'mono'], ['FESR, FSE+, FEASR'], ['€ 2.000 / mese', 'mono']], 'Gestisci'),
          riga('Organismo intermedio A', 'Delega su misure di formazione', 'ok', [['17', 'mono'], ['FSE+'], ['€ 600 / mese', 'mono']], 'Gestisci'),
          riga('Organismo intermedio B', 'Delega su sviluppo rurale', 'att', [['7', 'mono'], ['FEASR'], ['€ 300 / mese — 82% usato', 'att']], 'Gestisci'),
        ] },
      { titolo: 'Ruoli e permessi', nota: 'I permessi si concedono al ruolo, mai alla persona: una persona cambia mansione, il ruolo resta.',
        colonne: ['RUOLO', 'PUÒ FARE', 'NON PUÒ FARE', 'UTENTI', ''],
        righe: [
          riga('Funzionario AdG', 'funzionario_adg', 'ok', [['redigere, citare il corpus, inviare a revisione'], ['protocollare da solo, pubblicare snapshot'], ['38', 'mono']], 'Modifica'),
          riga('Beneficiario', 'beneficiario_ri', 'ok', [['rendicontare le proprie spese'], ['vedere atti di altri, redigere'], ['19', 'mono']], 'Modifica'),
          riga('Platform admin', 'platform_admin', 'ok', [['governare la piattaforma, pubblicare snapshot'], ['firmare atti amministrativi'], ['5', 'mono']], 'Modifica'),
          riga('Tenant admin', 'tenant_admin', 'ok', [['governare il proprio tenant'], ['toccare tetti e corpora di altri tenant'], ['3', 'mono']], 'Modifica'),
          riga('Auditor', 'non ancora presente nel realm', 'ko', [['—'], ['—'], ['0', 'mono']], 'Crea'),
        ] },
    ],
    avvisi: [],
  },

  /* ---------- 2 · CONOSCENZA ---------- */
  'co-fonti': {
    gruppo: '2 · CONOSCENZA', nome: 'Fonti, gerarchia, vigenza',
    titolo: 'Fonti, gerarchia e vigenza',
    lede: 'Quale fonte prevale quando due dicono cose diverse, e quali non sono più citabili come base giuridica. È la parte del sistema che tiene un atto in piedi davanti a un auditor.',
    azioni: [['Aggiungi una fonte', 'primaria'], ['Vedi il grafo', 'secondaria']],
    kpi: [
      ['Fonti nel corpus', '312', 'su tre programmi'],
      ['Norme abrogate', '38', 'presenti ma non citabili', 'ko'],
      ['Contrasti dichiarati', '4', 'fra fonti di pari rango', 'att'],
      ['Citazioni verificate', '96%', 'con riferimento puntuale'],
    ],
    blocchi: [
      { titolo: 'Gerarchia delle fonti', nota: 'In caso di contrasto prevale il rango superiore. Fra fonti di pari rango il sistema dichiara il contrasto e non decide al posto del funzionario.',
        colonne: ['RANGO', 'ESEMPI', 'FONTI', 'PREVALENZA', ''],
        righe: [
          riga('1 — Diritto dell\'Unione', 'Regolamenti, decisioni, atti delegati', 'ok', [['Reg. 2021/1060, 2021/1058, 2024/1689'], ['61', 'mono'], ['prevale su tutto']], 'Apri'),
          riga('2 — Normativa nazionale', 'Leggi, decreti legislativi', 'ok', [['L. 241/1990, D.lgs. 36/2023, L. 132/2025'], ['54', 'mono'], ['cede al diritto UE']], 'Apri'),
          riga('3 — Atti dell\'ente', 'Programmi, delibere, avvisi attuativi', 'ok', [['Programmi regionali, avvisi, delibere'], ['118', 'mono'], ['cede a 1 e 2']], 'Apri'),
          riga('4 — Prassi e linee guida', 'Circolari, manuali, orientamenti', 'ok', [['Linee guida IGRUE, manuale dei controlli'], ['79', 'mono'], ['interpreta, non fonda']], 'Apri'),
        ] },
      { tipo: 'schede', titolo: 'Contrasti aperti', nota: 'Dichiarati al funzionario nel momento in cui redige, non risolti d\'ufficio.',
        schede: [
          ['Ammissibilità delle spese di personale', 'FSE+', 'Le linee guida IGRUE e il manuale dei controlli dell\'ente indicano soglie diverse. Prevale la fonte di rango superiore: nessuna delle due lo è.', 'att'],
          ['Termine per la conclusione dell\'istruttoria', 'FESR', 'L. 241/1990 e avviso attuativo indicano 90 e 60 giorni. Prevale la legge, ma l\'avviso è vincolante per l\'ente.', 'att'],
          ['Obbligo di pubblicazione degli esclusi', 'FSE+', 'Contrasto fra due delibere dello stesso rango, entrambe vigenti.', 'att'],
        ] },
      { titolo: 'Vigenza: le uscite recenti', nota: 'Una norma abrogata resta nel corpus come richiamo storico. Citarla come base giuridica è un errore che la verifica segnala.',
        colonne: ['FONTE', 'STATO', 'SOSTITUITA DA', 'CITAZIONI DA CORREGGERE', ''],
        righe: [
          riga('D.lgs. 163/2006', 'Vecchio codice dei contratti', 'ko', [['abrogata', 'ko'], ['D.lgs. 36/2023'], ['0', 'mono']], 'Apri'),
          riga('Reg. (UE) 1303/2013', 'Programmazione 2014–2020', 'ko', [['non vigente per il ciclo attuale', 'ko'], ['Reg. (UE) 2021/1060'], ['2', 'mono']], 'Correggi'),
          riga('Delibera 88/2024', 'Criteri di selezione superati', 'ko', [['sostituita', 'ko'], ['Delibera 114/2026'], ['1', 'mono']], 'Correggi'),
          riga('Art. 12, D.lgs. 36/2023', 'Abrogato dal D.lgs. 96/2026', 'att', [['abrogazione parziale in corso', 'att'], ['D.lgs. 96/2026'], ['4', 'mono']], 'Propaga'),
        ] },
    ],
    avvisi: [['ko', 'Sette citazioni puntano a norme non più vigenti', 'Compaiono in atti già chiusi e in due bozze in lavorazione. Le bozze vanno corrette prima dell\'invio a revisione; gli atti chiusi restano come sono, con nota nel fascicolo.', 'Vedi le citazioni']],
  },

  'co-ingest': {
    gruppo: '2 · CONOSCENZA', nome: 'Ingest e indicizzazione',
    titolo: 'Ingest e indicizzazione',
    lede: 'Lo stato dei lavori in corso sul corpus: che cosa sta entrando, che cosa si sta reindicizzando, che cosa è fermo. Per far entrare fonti nuove si passa dalla pagina di alimentazione.',
    azioni: [['Alimenta il corpus', 'primaria', '/admin/corpus/ingestione'], ['Nuovo job', 'secondaria']],
    kpi: [
      ['Job in corso', '2', 'una reindicizzazione, un ingest'],
      ['Job in errore', '1', 'FEASR, fermo da 3 giorni', 'ko'],
      ['Documenti fuori dal corpus', '14', 'errore OCR su PDF scansionati', 'ko'],
      ['Chunk totali', '167.416', 'sui tre programmi'],
    ],
    blocchi: [
      { titolo: 'Job', nota: 'Un job non in errore ma senza avanzamento è più insidioso di un job fallito: non genera allarmi.',
        colonne: ['JOB', 'AVANZAMENTO', 'DURATA', 'ESITO', ''],
        righe: [
          riga('Reindicizzazione FSE+', 'Snapshot v8 → v9 · embedding e5-large-v2', 'att', [['41%', 'mono'], ['1h 12m'], ['fermo da 12 minuti, nessun errore', 'att']], 'Vedi log'),
          riga('Ingest FEASR', '14 PDF scansionati · OCR + chunking', 'ko', [['fallito', 'mono'], ['3 giorni'], ['errore nel layer OCR', 'ko']], 'Riprova'),
          riga('Ingest FESR', '18 documenti dal canale EUR-Lex', 'ok', [['completato', 'mono'], ['22m'], ['snapshot v12 pubblicato']], 'Apri'),
          riga('Verifica citazioni', 'Controllo dei riferimenti su tutti gli atti chiusi', 'ok', [['completato', 'mono'], ['6m'], ['7 citazioni da correggere', 'att']], 'Apri'),
        ] },
      { tipo: 'lista', titolo: 'Parametri di indicizzazione', nota: 'Cambiarli richiede una reindicizzazione completa del corpus interessato.',
        voci: [
          ['Strategia di chunking', 'Semantica, con ricaduta sui confini di articolo.', '800 / 120', 'ok'],
          ['Modello di embedding', 'On-premise: nessun testo di atto esce dall\'infrastruttura.', 'e5-large-v2', 'ok'],
          ['Reranker', 'Attivo solo sulle interrogazioni del corpus, non sull\'ingest.', 'bge-reranker-v2', 'ok'],
          ['Nodi GPU disponibili', 'Uno dei tre non risponde: la reindicizzazione ne subisce il rallentamento.', '2 su 3', 'att'],
          ['Timeout dei job', 'Nessun timeout configurato: un job può restare in attesa a tempo indefinito.', 'assente', 'ko'],
        ] },
    ],
    avvisi: [['ko', 'Le risposte sul FEASR sono incomplete e non lo dichiarano', '14 documenti sono fuori dal corpus da tre giorni. Il sistema risponde comunque, senza avvertire che la base documentale è parziale: è il difetto più grave in circolazione.', 'Risolvi']],
  },

  /* ---------- 3 · INTELLIGENZA ---------- */
  'in-gateway': {
    gruppo: '3 · INTELLIGENZA', nome: 'Gateway di inferenza',
    titolo: 'Gateway di inferenza',
    lede: 'Tutto il traffico verso i modelli passa da qui. È il punto in cui il vincolo sui dati sensibili viene applicato: non è una raccomandazione all\'utente, è un instradamento che non si può aggirare.',
    azioni: [['Modifica le regole', 'primaria'], ['Registro delle chiamate', 'secondaria']],
    kpi: [
      ['Richieste 24h', '2.914', 'su tre tenant'],
      ['Latenza p95', '3,2 s', 'in salita: un nodo GPU è fuori', 'att'],
      ['Dirottamenti', '61', 'per tetto di spesa raggiunto', 'att'],
      ['Fughe verso il cloud', '0', 'su richieste con dati sensibili'],
    ],
    blocchi: [
      { tipo: 'lista', titolo: 'Regole di instradamento', nota: 'Valutate in quest\'ordine: la prima che corrisponde decide. Le prime due non sono disattivabili dall\'interfaccia.',
        voci: [
          ['Dati sensibili → solo on-premise', 'Vincolo di piattaforma: nessuna deroga per tenant.', 'sempre attiva', 'ok'],
          ['Testo di atti in lavorazione → solo on-premise', 'Una bozza non protocollata non esce dall\'infrastruttura.', 'sempre attiva', 'ok'],
          ['Redazione assistita → Llama 3.3 70B', 'Qualità sufficiente e costo nullo per token.', 'attiva', 'ok'],
          ['Verifica di conformità → Qwen2.5 72B', 'Resa migliore sui confronti puntuali fra testo e norma.', 'attiva', 'ok'],
          ['Ricerca normativa lunga → cloud UE', 'Consentito: la domanda non contiene testo dell\'atto.', 'attiva', 'ok'],
          ['Fallback se un modello non risponde', 'Il modello di ripiego viene registrato nel fascicolo.', 'attiva', 'ok'],
        ] },
      { titolo: 'Traffico per tipo di richiesta', nota: 'Il costo per token vale solo sui modelli cloud: gli on-premise costano capacità, non denaro.',
        colonne: ['TIPO DI RICHIESTA', 'MODELLO', 'RICHIESTE', 'ESITO', ''],
        righe: [
          riga('Redazione assistita', 'proposte di testo con fonti', 'ok', [['Llama 3.3 70B — on-prem'], ['1.284', 'mono'], ['p95 2,8 s']], 'Apri'),
          riga('Verifica di conformità', 'confronto fra atto e check-list', 'ok', [['Qwen2.5 72B — on-prem'], ['612', 'mono'], ['p95 4,1 s']], 'Apri'),
          riga('Interrogazione del corpus', 'domande sulla normativa', 'ok', [['Llama 3.3 70B — on-prem'], ['703', 'mono'], ['p95 1,9 s']], 'Apri'),
          riga('Ricerca normativa estesa', 'sintesi su più fonti', 'att', [['Mistral Large — cloud UE'], ['254', 'mono'], ['dirottata su on-prem', 'att']], 'Apri'),
          riga('Riconoscimento documenti', 'tipo di atto e metadati in ingest', 'ok', [['Qwen2.5 72B — on-prem'], ['61', 'mono'], ['p95 6,4 s']], 'Apri'),
        ] },
    ],
    avvisi: [['att', 'Un nodo GPU su tre non risponde e non ha generato allarme', 'Il gateway ha smesso di instradare verso quel nodo senza segnalarlo: le richieste non vanno in errore, ma la latenza p95 è salita a 3,2 secondi.', 'Vedi i nodi']],
  },

  'in-modelli': {
    gruppo: '3 · INTELLIGENZA', nome: 'Modelli e routing',
    titolo: 'Modelli e routing',
    lede: 'Quali modelli sono disponibili, dove girano e quanto costano. La colonna che conta davvero è la sede: un modello fuori dall\'infrastruttura non può vedere il testo degli atti.',
    azioni: [['Aggiungi un modello', 'primaria'], ['Confronta due modelli', 'secondaria']],
    kpi: [
      ['Modelli disponibili', '5', 'tre on-premise, due cloud UE'],
      ['Traffico on-premise', '73%', 'in crescita per il dirottamento'],
      ['Occupazione GPU', '68%', 'su due nodi invece di tre', 'att'],
      ['Spesa cloud del mese', '€ 500', 'tetto raggiunto su Mistral', 'ko'],
    ],
    blocchi: [
      { titolo: 'Modelli', nota: 'La sede non è un dettaglio di deployment: è ciò che rende un modello ammissibile per una certa richiesta.',
        colonne: ['MODELLO', 'SEDE', 'USO', 'COSTO', ''],
        righe: [
          riga('Llama 3.3 70B', 'Modello aperto — servito su GPU proprie', 'ok', [['on-premise'], ['redazione, interrogazione corpus'], ['capacità', 'mono']], 'Configura'),
          riga('Qwen2.5 72B', 'Modello aperto — servito su GPU proprie', 'ok', [['on-premise'], ['verifica, riconoscimento'], ['capacità', 'mono']], 'Configura'),
          riga('Mistral Small', 'Modello aperto — servito su GPU proprie', 'ok', [['on-premise'], ['classificazione, estrazione metadati'], ['capacità', 'mono']], 'Configura'),
          riga('Mistral Large', 'Servizio gestito in Unione Europea', 'ko', [['cloud UE'], ['ricerca normativa estesa'], ['€ 500 / 500', 'ko']], 'Alza il tetto'),
          riga('Modello di riserva', 'Servizio gestito in Unione Europea — solo fallback', 'ok', [['cloud UE'], ['solo se un on-premise non risponde'], ['€ 0 / 200', 'mono']], 'Configura'),
        ] },
      { tipo: 'schede', titolo: 'Capacità di calcolo', nota: 'La capacità è il vero limite della piattaforma: quando manca, la coda si allunga senza generare errori.',
        schede: [
          ['Nodo GPU 1', '71% · attivo', '4× H100 · Llama 3.3 e Qwen2.5 in memoria.', 'ok'],
          ['Nodo GPU 2', '65% · attivo', '4× H100 · Qwen2.5 e Mistral Small.', 'ok'],
          ['Nodo GPU 3', 'non risponde', 'Uscito dall\'instradamento senza allarme, da 4 ore.', 'ko'],
        ] },
    ],
    avvisi: [['att', 'Il modello può cambiare a metà lavorazione di uno stesso atto', 'Il dirottamento per tetto di spesa funziona, ma un atto iniziato con Mistral Large può proseguire su un modello on-premise. Il fascicolo registra entrambi: va deciso se è accettabile.', 'Decidi la regola']],
  },

  'in-skill': {
    gruppo: '3 · INTELLIGENZA', nome: 'Libreria skill',
    titolo: 'Libreria skill',
    lede: 'Una skill è un compito circoscritto con un proprio prompt, un proprio modello e una propria soglia di qualità. Sotto la soglia una skill non deve poter essere invocata in produzione.',
    azioni: [['Nuova skill', 'primaria'], ['Esegui le valutazioni', 'secondaria']],
    kpi: [
      ['Skill attive', '4', 'più una in bozza'],
      ['Invocazioni 24h', '531', 'su tre tenant'],
      ['Sotto soglia', '1', 'risposta a rilievo di audit', 'att'],
      ['Soglia di ammissibilità', '0,90', 'sotto: uso vietato in produzione'],
    ],
    blocchi: [
      { titolo: 'Skill', nota: 'Il punteggio è una valutazione automatica su un set di casi verificati a mano: misura la conformità delle citazioni, non l\'eleganza del testo.',
        colonne: ['SKILL', 'MODELLO', 'PUNTEGGIO', 'INVOCAZIONI', ''],
        righe: [
          riga('Redazione di avvisi', 'Prompt v7 · corpus FESR e FSE+', 'ok', [['Llama 3.3 70B'], ['0,95', 'mono'], ['214', 'mono']], 'Apri'),
          riga('Verifica di conformità', 'Prompt v5 · check-list del manuale dei controlli', 'ok', [['Qwen2.5 72B'], ['0,94', 'mono'], ['186', 'mono']], 'Apri'),
          riga('Estrazione della base giuridica', 'Prompt v4 · gerarchia delle fonti', 'ok', [['Qwen2.5 72B'], ['0,93', 'mono'], ['87', 'mono']], 'Apri'),
          riga('Risposta a rilievo di audit', 'Prompt v3 in bozza · corpus FESR', 'att', [['Llama 3.3 70B'], ['0,81', 'att'], ['44', 'mono']], 'Sospendi'),
          riga('Relazione di monitoraggio', 'Prompt v1 · mai valutata', 'neutro', [['non assegnato'], ['—', 'mono'], ['0', 'mono']], 'Valuta'),
        ] },
      { tipo: 'lista', titolo: 'Perché la skill di audit è sotto soglia', nota: 'Le altre tre superano 0,93 con lo stesso modello e lo stesso corpus: il problema non è il modello.',
        voci: [
          ['Citazioni valide', 'Le altre skill stanno sopra il 96%.', '88%', 'att'],
          ['Riferimenti puntuali all\'articolo', 'Cita l\'atto senza indicare il comma.', '74%', 'ko'],
          ['Casi di valutazione', 'Troppo pochi per un giudizio stabile.', '18', 'att'],
          ['Ultima revisione del prompt', 'Il prompt v3 è ancora in bozza.', '20:40 di ieri', 'ok'],
        ] },
    ],
    avvisi: [['att', 'Una skill sotto soglia è ancora invocabile in staging', 'La soglia di 0,90 è dichiarata ma non applicata: nulla impedisce tecnicamente di usarla. Va reso un blocco, non un avviso.', 'Applica la soglia']],
  },

  /* ---------- 4 · PRODUZIONE ---------- */
  'pr-atti': {
    gruppo: '4 · PRODUZIONE', nome: 'Atti e redazione',
    titolo: 'Atti e redazione',
    lede: 'Tutti gli atti in lavorazione sulla piattaforma. Nessuno di questi è protocollabile prima di una validazione umana registrata: la coda di revisione è il collo di bottiglia del flusso.',
    azioni: [['Assegna i revisori', 'primaria'], ['Archivio atti', 'secondaria']],
    kpi: [
      ['In lavorazione', '23', 'su tre programmi'],
      ['In attesa di revisione', '7', 'la più vecchia da 9 giorni', 'att'],
      ['Con problemi bloccanti', '2', 'base giuridica o allegati mancanti', 'ko'],
      ['Passaggi assistiti', '412', 'ognuno con la sua fonte'],
    ],
    blocchi: [
      { titolo: 'Atti che richiedono una decisione', nota: 'Ordinati per impatto, non per data. Un atto validato e non protocollato è lavoro finito che non produce effetti.',
        colonne: ['ATTO', 'PROGRAMMA', 'STATO', 'DA QUANTO', ''],
        righe: [
          riga('Decreto 412/2026 — ammissione', 'Manca la base giuridica per l\'Allegato B', 'ko', [['FSE+'], ['bozza · non protocollabile', 'ko'], ['4 giorni']], 'Apri'),
          riga('Avviso efficienza PMI', 'In redazione al 72% · due redattori', 'att', [['FESR'], ['in redazione', 'att'], ['2 ore']], 'Apri'),
          riga('Determina 407/2026 — impegno', 'Validata da g.rizzo, pronta per il protocollo', 'ok', [['FESR'], ['in attesa di protocollo', 'att'], ['1 giorno']], 'Apri'),
          riga('Verbale istruttoria 9', 'Prodotto dalla skill di verifica', 'att', [['FSE+'], ['in attesa di revisione', 'att'], ['9 giorni']], 'Assegna'),
          riga('Relazione di monitoraggio 2° trim.', 'Scadenza di trasmissione fra 6 giorni', 'att', [['FEASR'], ['in attesa di revisione', 'att'], ['5 giorni']], 'Assegna'),
        ] },
      { tipo: 'schede', titolo: 'La coda di revisione', nota: 'Non è un problema tecnico: nessuno presidia la coda perché non esiste un meccanismo di assegnazione.',
        schede: [
          ['In attesa da oltre 7 giorni', '2 atti', 'Nessun revisore assegnato, nessun sollecito inviato.', 'ko'],
          ['In attesa da 3 a 7 giorni', '3 atti', 'Revisori con il permesso corretto ma non avvisati.', 'att'],
          ['Validati nelle 24h', '4 atti', 'Tutti da g.rizzo: la revisione poggia su una persona.', 'att'],
        ] },
    ],
    avvisi: [['att', 'Sette bozze attendono una revisione che nessuno ha in carico', 'I revisori hanno il permesso corretto: manca l\'assegnazione. Va progettata con scadenze e solleciti, senza trasformare la validazione in un timbro automatico.', 'Progetta la coda']],
  },

  'pr-cowork': {
    gruppo: '4 · PRODUZIONE', nome: 'Spazi di lavoro condivisi',
    titolo: 'Spazi di lavoro condivisi',
    lede: 'Un atto complesso lo scrivono più persone, spesso di enti diversi. Lo spazio condiviso tiene insieme documento, allegati e decisioni prese, con la traccia di chi ha fatto cosa.',
    azioni: [['Nuovo spazio', 'primaria'], ['Vedi gli archivi', 'secondaria']],
    kpi: [
      ['Spazi attivi', '2', 'un terzo archiviato ieri'],
      ['Persone al lavoro ora', '3', 'in due spazi'],
      ['Modifiche 24h', '148', 'tutte attribuite'],
      ['Conflitti irrisolti', '1', 'due versioni dello stesso articolo', 'att'],
    ],
    blocchi: [
      { titolo: 'Spazi', nota: 'Chi partecipa a uno spazio vede il documento, non il resto del tenant: il perimetro è lo spazio, non l\'ente.',
        colonne: ['SPAZIO', 'PARTECIPANTI', 'ATTIVITÀ', 'ULTIMA MODIFICA', ''],
        righe: [
          riga('Avviso efficienza PMI', 'FESR · con Organismo intermedio A', 'ok', [['3 — c.lentini, g.rizzo, m.parisi'], ['redazione in corso'], ['2 minuti fa']], 'Entra'),
          riga('Relazione di monitoraggio', 'FEASR · solo Autorità di Gestione', 'att', [['2 — a.ferro, c.lentini'], ['un conflitto da risolvere', 'att'], ['1 ora fa']], 'Entra'),
          riga('Bando formazione 2/2026', 'FSE+ · archiviato dopo la protocollazione', 'neutro', [['4 partecipanti'], ['chiuso'], ['ieri, 18:20']], 'Apri'),
        ] },
      { tipo: 'lista', titolo: 'Regole dello spazio', nota: 'Servono a rendere ricostruibile una scrittura a più mani, non a limitarla.',
        voci: [
          ['Attribuzione delle modifiche', 'Ogni paragrafo porta l\'autore e l\'ora: è la base del fascicolo.', 'sempre attiva', 'ok'],
          ['Modifica contemporanea dello stesso paragrafo', 'Consentita, con conflitto dichiarato invece di sovrascrittura.', 'consentita', 'ok'],
          ['Proposte dell\'assistente', 'Vanno confermate da una persona, sempre e da chiunque partecipi.', 'conferma obbligatoria', 'ok'],
          ['Partecipanti esterni al tenant', 'Ammessi solo su invito e solo sullo spazio, mai sul corpus.', 'su invito', 'ok'],
        ] },
    ],
    avvisi: [],
  },

  'pr-integrazioni': {
    gruppo: '4 · PRODUZIONE', nome: 'Integrazioni e protocollo',
    titolo: 'Integrazioni e protocollo',
    lede: 'I sistemi con cui UranIA deve parlare per produrre effetti giuridici. Senza il protocollo informatico la piattaforma redige atti che non possono essere registrati: è il difetto che la blocca in produzione.',
    azioni: [['Configura il protocollo', 'primaria'], ['Prova le connessioni', 'secondaria']],
    kpi: [
      ['Integrazioni attive', '2', 'editor documentale e identità'],
      ['Non configurate', '1', 'protocollo informatico', 'ko'],
      ['Atti in attesa di registrazione', '1', 'validato, non protocollabile', 'ko'],
      ['Ultimo handshake riuscito', '21:57', 'OnlyOffice v8.1'],
    ],
    blocchi: [
      { titolo: 'Sistemi collegati', nota: 'Un connettore non configurato non è un\'assenza di funzionalità: è un atto che non produce effetti.',
        colonne: ['SISTEMA', 'FUNZIONE', 'STATO', 'ULTIMO CONTATTO', ''],
        righe: [
          riga('Protocollo informatico', 'Registrazione e segnatura degli atti', 'ko', [['obbligatorio per la produzione'], ['non configurato', 'ko'], ['nessun handshake', 'ko']], 'Configura'),
          riga('OnlyOffice Document Server', 'Modifica dei documenti nel browser', 'ok', [['v8.1'], ['attivo'], ['21:57']], 'Apri'),
          riga('Keycloak', 'Identità digitale e sessioni', 'ok', [['realm urania-test'], ['attivo'], ['21:57']], 'Apri'),
          riga('Banche dati di verifica', 'Controlli su beneficiari e antimafia', 'neutro', [['previsto, non avviato'], ['non configurato', 'neutro'], ['—']], 'Pianifica'),
          riga('Sistema di monitoraggio nazionale', 'Trasmissione dei dati di attuazione', 'neutro', [['previsto, non avviato'], ['non configurato', 'neutro'], ['—']], 'Pianifica'),
        ] },
      { tipo: 'lista', titolo: 'Che cosa serve per configurare il protocollo', nota: 'Raccolto qui perché è il lavoro che sblocca la produzione.',
        voci: [
          ['Endpoint del servizio di segnatura', 'Da richiedere al responsabile della gestione documentale.', 'mancante', 'ko'],
          ['Credenziali di servizio', 'Utenza applicativa dedicata, non personale.', 'mancante', 'ko'],
          ['Mappatura dei metadati', 'Corrispondenza fra campi dell\'atto e campi di segnatura.', 'da definire', 'att'],
          ['Gestione degli errori e ritentativi', 'Un atto non registrato deve tornare in coda, non scomparire.', 'da progettare', 'att'],
        ] },
    ],
    avvisi: [['ko', 'Il protocollo informatico non è configurato', 'Nessun atto può essere registrato: la piattaforma non è utilizzabile in produzione. È il primo lavoro da chiudere, prima di qualunque miglioramento.', 'Configura']],
  },

  /* ---------- TRASVERSALI ---------- */
  'tr-tracciabilita': {
    gruppo: 'TRASVERSALI', nome: 'Tracciabilità e AI Act',
    titolo: 'Tracciabilità e AI Act',
    lede: 'Per ogni atto assistito la piattaforma conserva le fonti citate, la persona che ha validato e la motivazione della decisione. È ciò che si consegna a un auditor quando chiede come è stato prodotto un atto.',
    azioni: [['Esporta un fascicolo', 'primaria'], ['Vedi l\'informativa', 'secondaria', '/trasparenza-ia']],
    kpi: [
      ['Atti con fascicolo completo', '100%', 'su 412 passaggi assistiti'],
      ['Citazioni con riferimento puntuale', '96%', 'il resto rinvia all\'atto intero', 'att'],
      ['Validazioni umane registrate', '38', 'nessun atto chiuso senza firma'],
      ['Fascicoli esportati', '4', 'per due richieste di audit'],
    ],
    blocchi: [
      { tipo: 'lista', titolo: 'Che cosa contiene il fascicolo', nota: 'Non tutto ciò che sarebbe tecnicamente registrabile va registrato: il prompt e i parametri restano fuori per scelta, il testo di lavoro non si conserva.',
        voci: [
          ['Fonti citate con riferimento puntuale', 'Atto, articolo e comma, con la versione dell\'archivio.', 'registrato', 'ok'],
          ['Revisore umano che ha validato', 'Nome, ruolo, data e ora della validazione.', 'registrato', 'ok'],
          ['Motivazione della decisione', 'Testo scritto dal funzionario, non generato.', 'registrato', 'ok'],
          ['Versione del corpus al momento della risposta', 'Rende la risposta riproducibile a distanza di mesi.', 'registrato', 'ok'],
          ['Modello e versione che ha risposto', 'Compreso il modello di ripiego in caso di fallback.', 'registrato', 'ok'],
          ['Prompt e parametri', 'Fuori dal fascicolo per scelta: non aggiungono nulla all\'auditor.', 'non registrato', 'neutro'],
        ] },
      { titolo: 'Obblighi di trasparenza', nota: 'Reg. UE 2024/1689 (art. 50) e L. 132/2025. Sono adempimenti verificabili, non dichiarazioni di intenti.',
        colonne: ['OBBLIGO', 'COME È ASSOLTO', 'STATO', 'VERIFICATO', ''],
        righe: [
          riga('Informare che si interagisce con un\'IA', 'Avviso presente in ogni schermata, login comprese', 'ok', [['avviso persistente'], ['assolto'], ['oggi']], 'Apri'),
          riga('Marcare i contenuti generati', 'Ogni paragrafo assistito è marcato con le sue fonti', 'ok', [['marcatura per paragrafo'], ['assolto'], ['oggi']], 'Apri'),
          riga('Garantire la sorveglianza umana', 'Nessun atto protocollabile senza validazione registrata', 'ok', [['blocco tecnico'], ['assolto'], ['oggi']], 'Apri'),
          riga('Conservare la tracciabilità', 'Fascicolo per atto, esportabile per l\'auditor', 'ok', [['fascicolo completo'], ['assolto'], ['oggi']], 'Apri'),
          riga('Dichiarare i limiti del sistema', 'Le risposte sono proposte da verificare, non accertamenti', 'att', [['dichiarato nell\'interfaccia'], ['da formalizzare', 'att'], ['—']], 'Completa'),
        ] },
    ],
    avvisi: [['att', 'Il 4% delle citazioni rinvia all\'atto intero', 'Diciassette citazioni indicano il regolamento senza l\'articolo. Sono formalmente valide ma inutilizzabili in audit: chi verifica deve poter arrivare al comma.', 'Vedi le citazioni']],
  },

  'tr-costi': {
    gruppo: 'TRASVERSALI', nome: 'Costi e capacità',
    titolo: 'Costi e capacità',
    lede: 'Due limiti diversi che si confondono facilmente: il denaro speso sui modelli cloud e la capacità di calcolo delle GPU proprie. Il primo genera un tetto, il secondo una coda.',
    azioni: [['Modifica i tetti', 'primaria'], ['Esporta il consumo', 'secondaria']],
    kpi: [
      ['Spesa del mese', '€ 1.240', 'su un tetto di € 2.000'],
      ['Token elaborati', '28,4 M', 'l\'82% su modelli on-premise'],
      ['Tenant sopra l\'80%', '1', 'Organismo intermedio B', 'att'],
      ['Costo medio per atto', '€ 0,54', 'in calo per il dirottamento'],
    ],
    blocchi: [
      { titolo: 'Consumo per tenant', nota: 'Il tetto non blocca il lavoro: dirotta il traffico sui modelli on-premise, con un allungamento dei tempi.',
        colonne: ['TENANT', 'SPESA / TETTO', 'TOKEN', 'ATTI PRODOTTI', ''],
        righe: [
          riga('Autorità di Gestione', 'Tenant principale', 'ok', [['€ 740 / 2.000', 'mono'], ['19,1 M', 'mono'], ['14', 'mono']], 'Apri'),
          riga('Organismo intermedio A', 'Misure di formazione', 'ok', [['€ 254 / 600', 'mono'], ['6,2 M', 'mono'], ['6', 'mono']], 'Apri'),
          riga('Organismo intermedio B', 'Sviluppo rurale — avviso già inviato', 'att', [['€ 246 / 300 · 82%', 'att'], ['3,1 M', 'mono'], ['3', 'mono']], 'Alza il tetto'),
        ] },
      { tipo: 'schede', titolo: 'Capacità, non denaro', nota: 'Un esaurimento di capacità non produce errori: allunga la coda in silenzio. È il motivo per cui va sorvegliato a parte.',
        schede: [
          ['Occupazione GPU', '68%', 'Su due nodi invece di tre: il terzo non risponde.', 'att'],
          ['Coda di inferenza', '4 richieste', 'Attesa media 1,2 s. Sopra le 20 la redazione diventa scomoda.', 'ok'],
          ['Job di indicizzazione in attesa', '1', 'La reindicizzazione FSE+ contende le GPU all\'inferenza.', 'att'],
        ] },
    ],
    avvisi: [['att', 'Un tenant è all\'82% del proprio tetto', 'L\'avviso è stato inviato al referente. Al raggiungimento il traffico verrà dirottato sui modelli on-premise: il lavoro continua, i tempi si allungano.', 'Rivedi il tetto']],
  },

  'tr-eventi': {
    gruppo: 'TRASVERSALI', nome: 'Registro eventi',
    titolo: 'Registro eventi',
    lede: 'Ogni azione rilevante sulla piattaforma, in ordine di tempo e attribuita a chi l\'ha compiuta. È la fonte a cui si torna quando si deve ricostruire che cosa è accaduto e quando.',
    azioni: [['Esporta il registro', 'primaria'], ['Filtra per attore', 'secondaria']],
    kpi: [
      ['Eventi 24h', '1.884', 'tutti attribuiti'],
      ['Accessi anomali', '0', 'nessun blocco per tentativi'],
      ['Modifiche di configurazione', '2', 'entrambe da platform_admin'],
      ['Conservazione', '24 mesi', 'poi archiviazione a lungo termine'],
    ],
    blocchi: [
      { titolo: 'Eventi recenti', nota: 'Il registro non è filtrabile per contenuto degli atti: registra le azioni, non il testo.',
        colonne: ['EVENTO', 'ATTORE', 'OGGETTO', 'ORA', ''],
        righe: [
          riga('Validazione di un atto', 'Determina 407/2026 dichiarata pronta per il protocollo', 'ok', [['g.rizzo · Funzionario AdG'], ['determina 407/2026'], ['21:58', 'mono']], 'Apri'),
          riga('Job in errore', 'Ingest FEASR fallito nel layer OCR', 'ko', [['sistema'], ['14 documenti'], ['21:40', 'mono']], 'Apri'),
          riga('Tetto di spesa raggiunto', 'Traffico dirottato sui modelli on-premise', 'att', [['sistema'], ['Mistral Large'], ['19:40', 'mono']], 'Apri'),
          riga('Modifica di un permesso', 'Aggiunto il permesso di rendicontazione a un ruolo', 'ok', [['admin@staging · platform_admin'], ['ruolo beneficiario_ri'], ['19:02', 'mono']], 'Apri'),
          riga('Pubblicazione di uno snapshot', 'Corpus FESR portato alla versione v12', 'ok', [['admin@staging · platform_admin'], ['corpus FESR'], ['18:12', 'mono']], 'Apri'),
          riga('Skill sotto soglia', 'Valutazione 0,81 sulla risposta a rilievo di audit', 'att', [['sistema'], ['skill audit'], ['20:40', 'mono']], 'Apri'),
        ] },
    ],
    avvisi: [],
  },
};
